import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut } from 'react-chartjs-2';
import { registerCharts, barOptions, doughnutOptions, barDataset, CHART } from '../components/ChartTheme';
import AppLayout from '../components/AppLayout';
import api from '../axios';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';

registerCharts();

export default function CursosDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/cursos/dashboard/estatisticas')
      .then(res => setStats(res.data))
      .catch(err => setErro(errorMsg(err) || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { icon: <MenuBookIcon />, value: stats?.cursos?.total, label: 'Cursos' },
    { icon: <CheckCircleIcon />, value: stats?.cursos?.ativos, label: 'Ativos' },
    { icon: <AssignmentIcon />, value: stats?.inscricoes?.total, label: 'Inscrições' },
    { icon: <TrendingUpIcon />, value: stats?.presencas?.taxa ? `${stats.presencas.taxa}%` : '—', label: 'Presença média' },
  ];

  const porMinisterio = Array.isArray(stats?.cursos_por_ministerio) ? stats.cursos_por_ministerio : [];
  const doughnutData = {
    labels: ['Ativos', 'Concluídos', 'Rascunhos'],
    datasets: [{ data: [stats?.cursos?.ativos || 0, stats?.cursos?.concluidos || 0, stats?.cursos?.rascunhos || 0], backgroundColor: [CHART.teal, CHART.lime, 'rgba(0,241,167,0.25)'], borderWidth: 0, hoverOffset: 6 }],
  };

  const atalhos = [
    { label: 'Cursos', icon: <MenuBookIcon />, path: '/cursos' },
    { label: 'Novo Curso', icon: <AddCircleIcon />, path: '/cursos/novo' },
    { label: 'Instrutores', icon: <PeopleIcon />, path: '/cursos/instrutores' },
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/cursos/dashboard' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: CHART.teal, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Módulo</Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, fontWeight: 800 }}>Cursos e Treinamentos</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>SaaS de cursos com ministérios, sessões, inscrições, presença e certificados.</Typography>
        </Box>

        {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
        {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

        {stats && (
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpis.map((k, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHART.teal, flexShrink: 0 }}>{k.icon}</Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '4.4rem', color: 'text.primary', lineHeight: 1 }}>{k.value ?? '—'}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={5}>
                <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3 }}>
                  <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>Status dos cursos</Typography>
                  <Box sx={{ height: 220 }}>
                    <Doughnut data={doughnutData} options={doughnutOptions()} />
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3 }}>
                  <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>Cursos por ministério</Typography>
                  <Box sx={{ height: 220 }}>
                    <Bar data={{ labels: porMinisterio.map(m => m.ministerio), datasets: [{ ...barDataset(porMinisterio.map(m => m.total)), label: 'Cursos' }] }} options={barOptions()} />
                  </Box>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Atalhos</Typography>
            <Grid container spacing={1.5}>
              {atalhos.map(a => (
                <Grid item xs={12} sm={6} md={3} key={a.path}>
                  <Button variant="outlined" onClick={() => navigate(a.path)} endIcon={<ArrowForwardIcon />} fullWidth
                    sx={{ justifyContent: 'space-between', borderColor: 'rgba(0,241,167,0.4)', color: CHART.teal, fontWeight: 700, borderRadius: 1, px: 2, py: 1.2, '&:hover': { borderColor: CHART.teal, bgcolor: 'rgba(0,241,167,0.08)' } }}>
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </AppLayout>
  );
}
